import { IoT } from "common-library/types/IoT"
import { Nullable } from "common-library/utils/types"

/**
 * 폼 옵션의 value 표현형. value === null ⇔ "use sensor's setting" (미설정).
 * 부모-자식 경계를 통과하는 body 필드는 null이 아니라 "키 자체 없음(undefined)"으로
 * 직렬화되므로, 미설정은 null | undefined | 키 없음 세 가지 인코딩으로 자유롭게 왕복한다.
 */
export type TrackingEngineFormOption<T> = { name: string; value: T | null }

export type TrackingEngineForm = Nullable<{
  tracker?: { value: IoT.TrackingEngine.Id | null }
  detectionPoint?: { value: IoT.Tracker.DetectionPoint | null }
}>

/** 역매핑 결과: option 목록이 채워진 폼 형태(각 필드에 name 보유). */
export type TrackingEngineFormWithOptions = {
  tracker?: TrackingEngineFormOption<IoT.TrackingEngine.Id>
  detectionPoint?: TrackingEngineFormOption<IoT.Tracker.DetectionPoint>
}

/**
 * form 내부 tracker/detectionPoint 필드를 backend-tracking-config body의
 * tracker/detectionPoint 필드로 매핑한다. 미설정(value==null)인 필드는 omit하여
 * payload를 최소화한다. 미설정 인코딩(null | undefined)을 body에서는 "키 없음"으로 정규화한다.
 */
export function form2TrackerBody(form: TrackingEngineForm): IoT.BackendTrackingConfig.VcaEdgeBody {
  return {
    ...(form?.tracker?.value != null ? { tracker: form.tracker.value } : {}),
    ...(form?.detectionPoint?.value != null ? { detectionPoint: form.detectionPoint.value } : {}),
  }
}

/**
 * 옵션 목록에서 body 값을 대응하는 form 옵션으로 매핑한다.
 *
 * - body 값이 미설정(null | undefined | 키 없음)이면 sensor-default 옵션(value===null)을 선택하고,
 *   없으면 undefined(구체 값으로 폴백하지 않음 → 왕복 가역성 보장).
 * - body 값이 설정되어 있으나 목록에 없으면(모르는 값) sensor-default, 그것도 없으면 첫 옵션으로 폴백.
 */
function resolveOption<T>(
  options: TrackingEngineFormOption<T>[],
  value: T | null | undefined
): TrackingEngineFormOption<T> | undefined {
  if (value == null) {
    return options.find((o) => o.value === null)
  }
  return (
    options.find((o) => o.value === value) ?? options.find((o) => o.value === null) ?? options[0]
  )
}

/** backend body → form 옵션 역매핑. form2TrackerBody의 역으로 왕복 가역성을 확보하는 경계 단일 진입점. */
export function trackerBody2Form(
  body: Nullable<IoT.BackendTrackingConfig.VcaEdgeBody> | undefined | null,
  opts: {
    trackers: TrackingEngineFormOption<IoT.TrackingEngine.Id>[]
    detectionPoints: TrackingEngineFormOption<IoT.Tracker.DetectionPoint>[]
  }
): TrackingEngineFormWithOptions {
  const tracker = resolveOption(opts.trackers, body?.tracker)
  const detectionPoint = resolveOption(opts.detectionPoints, body?.detectionPoint)

  return { tracker, detectionPoint }
}

/**
 * 목록에서 tracker 선택을 해석한다. 현재 값이 유효하면 그대로(보존), 아니면
 * 첫 번째 실제 엔진(value != null)으로 폴백한다. (EdgeFace/GroupFace 필터링 후 재선택 용)
 */
export function resolveTrackerSelection<T extends string>(
  options: TrackingEngineFormOption<T>[],
  current: T | null | undefined
): TrackingEngineFormOption<T> | undefined {
  if (current != null) {
    const match = options.find((o) => o.value === current)
    if (match) return match
  }
  return options.find((o) => o.value != null) ?? options[0]
}

/**
 * 목록에서 Detection Point 선택을 해석한다. 현재 값이 목록에 유효하면 그대로(보존),
 * 아니면 첫 번째 옵션(정렬 결과 Bottom→Center)으로 폴백한다. 목록이 비어 있으면 undefined.
 * (EdgeFace/GroupFace 활성 및 tracker 변경 시 DP 선택을 재계산하는 단일 진입점)
 */
export function resolveDetectionPointSelection<T extends string>(
  options: TrackingEngineFormOption<T>[],
  current: T | null | undefined
): TrackingEngineFormOption<T> | undefined {
  if (current != null) {
    const match = options.find((o) => o.value === current)
    if (match) return match
  }
  return options[0]
}

// Detection Point 표시 순서 (Bottom → Center). 목록에 없는 미지 값은 뒤로 정렬한다.
const DETECTION_POINT_ORDER: Record<string, number> = { bottom: 0, center: 1 }

/**
 * Detection Point 옵션 목록을 표시 순서(Bottom → Center)로 정렬한다.
 * 순서표에 없는 미지 값은 항상 목록 뒤로 보낸다(정렬 파괴 방지). 원본 배열은 변경하지 않는다.
 */
export function sortDetectionPointOptions<T extends string>(
  options: TrackingEngineFormOption<T>[]
): TrackingEngineFormOption<T>[] {
  return [...options].sort(
    (a, b) =>
      (DETECTION_POINT_ORDER[a.value ?? ""] ?? Number.MAX_SAFE_INTEGER) -
      (DETECTION_POINT_ORDER[b.value ?? ""] ?? Number.MAX_SAFE_INTEGER)
  )
}
